import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import UserSchema, { UserJSON, UserParams } from "users/user-schema";
import EntityMember from "_shared/entity-members/entity-member";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import type { UserOnboardingWork } from "users/information/user-onboarding-work";

export class User {
  public static get EXAMPLE(): User {
    return User.create({
      name: Name.create({ params: { firstName: "Test", lastName: "User" } }),
      email: EmailAddress.create("test@example.com"),
      id: Guid.generate(),
      isSuperUser: true,
      legalEntityId: Guid.generate(),
      companyName: "Test Company",
    });
  }

  public static create(values: UserParams): User {
    const parsed = UserSchema.validate(values);
    return new User(parsed);
  }

  public static fromJSON(json: unknown): User | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = UserSchema.deserialize(json);
    return new User(params);
  }

  public readonly id?: Guid;
  public readonly name: Name;
  public readonly email?: EmailAddress;
  public readonly created?: AHDate;
  public readonly isSuperUser: boolean = false;
  public readonly userIsDisabled: boolean = false;
  public readonly individualId?: Guid;
  public readonly companyName?: string;
  public readonly legalEntityId: Guid;
  public readonly isCompanyManager: boolean = false;
  /**
   * Whether this session holds an entity-wide notifications grant (issue #4438). The Ably token
   * the API mints carries the entity notifications channel only for a holder, so the app reads
   * this to avoid asking for a channel it would be refused; it is not itself the gate.
   */
  public readonly hasEntityWideNotificationsAccess: boolean = false;
  /**
   * Whether this user may read every grant in their organization (issue #6785). The Ably token
   * carries the organization's permissions channel only for such a user, so the app reads this
   * to avoid asking for a channel it would be refused; it is not itself the gate.
   */
  public readonly hasMemberPermissionsAccess: boolean = false;
  public readonly companyId?: Guid;
  public readonly onboardingWork?: UserOnboardingWork;

  public get initials(): string {
    return this.name.initials;
  }

  public get fullName(): string {
    return this.name.fullName;
  }

  public constructor(params: UserParams) {
    const validated = UserSchema.validate(params);
    Object.assign(this, validated);
    this.email = validated.email;
    this.legalEntityId = validated.legalEntityId;
    this.name = validated.name;
    this.companyName = validated.companyName ?? undefined;
    this.isSuperUser = validated.isSuperUser ?? false;
    this.userIsDisabled = validated.userIsDisabled ?? false;
    this.isCompanyManager = validated.hasSigningAuthority ?? false;
    this.hasEntityWideNotificationsAccess = validated.hasEntityWideNotificationsAccess ?? false;
    this.hasMemberPermissionsAccess = validated.hasMemberPermissionsAccess ?? false;
    this.onboardingWork = validated.onboardingWork;
  }

  public toJSON(): UserJSON {
    return UserSchema.serialize(this);
  }

  public isEqualTo(other: User): boolean {
    if (!this.id || !other?.id) return false;
    return this.id.isEqualTo(other.id);
  }

  public toString(): string {
    return `User (
      id: ${this.id?.value},
      name: ${this.name.toString()},
      email: ${this.email?.toString() ?? "undefined"},
      isSuperUser: ${this.isSuperUser},
      userIsDisabled: ${this.userIsDisabled},
      individualId: ${this.individualId?.value},
      companyName: ${this.companyName},
      legalEntityId: ${this.legalEntityId?.value},
      isCompanyManager: ${this.isCompanyManager},
      companyId: ${this.companyId?.value}
    )`;
  }

  public toEntityMember(): EntityMember {
    if (!this.id) {
      throw new Error("Cannot convert User to EntityMember without an ID.");
    }
    if (!this.legalEntityId) {
      throw new Error("Cannot convert User to EntityMember without a legalEntityId.");
    }
    return new EntityMember({
      userId: this.id,
      legalEntityId: this.legalEntityId,
    });
  }
}
